<template>
  <div class="crud_form_wrapper">
    <div class="form_title_wrapper">
      <h4>{{ $t("PLACEHOLDERS.addProvider") }}</h4>
    </div>
    <div class="col-12 text-end">
      <v-btn @click="$router.go(-1)" style="color: #ef3405">
        <i class="fas fa-backward"></i>
      </v-btn>
    </div>
    <div class="single_step_form_content_wrapper">
      <form @submit.prevent="validateFormInputs">
        <div class="row">
          <!-- Provider Personal Data Section -->
          <div class="col-12 mb-3">
            <h5 class="section-title">
              {{ $t("PLACEHOLDERS.provider_personal_data") }}
            </h5>
          </div>

          <!-- Name -->
          <base-input
            col="4"
            type="text"
            :placeholder="$t('PLACEHOLDERS.full_name')"
            v-model.trim="data.name"
            required
          />

          <!-- Mobile -->
          <base-input
            col="4"
            type="text"
            :placeholder="$t('PLACEHOLDERS.phone')"
            v-model.trim="data.mobile"
            required
          />

          <!-- Email -->
          <base-input
            col="4"
            type="email"
            :placeholder="$t('PLACEHOLDERS.email')"
            v-model.trim="data.email"
            required
          />

          <!-- Password -->
          <base-input
            col="6"
            type="password"
            :placeholder="$t('PLACEHOLDERS.password')"
            v-model.trim="data.password"
            required
          />

          <!-- Password Confirmation -->
          <base-input
            col="6"
            type="password"
            :placeholder="$t('PLACEHOLDERS.confirmPassword')"
            v-model.trim="data.password_confirmation"
            required
          />

          <!-- Avatar (Optional) -->
          <base-image-upload-input
            col="12"
            identifier="avatar"
            :placeholder="$t('PLACEHOLDERS.avatar')"
            @selectImage="selectAvatar"
          />

          <!-- Warehouse/Company Data Section -->
          <div class="col-12 mb-3 mt-4">
            <h5 class="section-title">
              {{ $t("PLACEHOLDERS.warehouse_company_data") }}
            </h5>
          </div>

          <!-- Activity Type -->
          <base-select-input
            col="6"
            :optionsList="activityTypes"
            :placeholder="$t('PLACEHOLDERS.activity_type')"
            v-model="data.activity_type"
            required
          />

          <!-- Warehouse Address -->
          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.warehouse_address')"
            v-model.trim="data.warehouse_address"
            required
          />

          <!-- Commercial Registration Image (Not for Individual) -->
          <base-image-upload-input
            col="6"
            identifier="commercial_registration"
            :placeholder="$t('PLACEHOLDERS.commercial_register_image')"
            @selectImage="selectCommercialImage"
          />

          <!-- Tax Registration Image (Not for Individual) -->
          <base-image-upload-input
            col="6"
            identifier="tax_registration"
            :placeholder="$t('PLACEHOLDERS.tax_registration_image')"
            @selectImage="selectTaxImage"
          />
          <!-- Address -->
          <base-input
            col="12"
            type="text"
            :placeholder="$t('PLACEHOLDERS.address')"
            v-model.trim="data.address"
            required
            disabled
          />

          <!-- Map Location -->
          <div class="col-12 mb-3">
            <p class="mb-2" style="color: #ef3405; font-size: 16px">
              {{ $t("PLACEHOLDERS.map_location")
              }}<span class="text-danger"> * </span>
            </p>

            <!-- Start:: Map Search Input -->
            <div class="col-12 mb-3">
              <input
                ref="searchInput"
                type="text"
                class="form-control"
                :placeholder="
                  $t('PLACEHOLDERS.search_location') ||
                  'Search for a location...'
                "
                style="
                  padding: 12px;
                  border: 1px solid #ddd;
                  border-radius: 4px;
                  width: 100%;
                "
              />
            </div>
            <!-- End:: Map Search Input -->

            <ChangableMapComponent
              :lat="parseFloat(data.lat)"
              :lng="parseFloat(data.lng)"
              @update:lat="data.lat = $event"
              @update:lng="data.lng = $event"
            />
          </div>

          <!-- Submit Button -->
          <div class="btn_wrapper">
            <base-button
              class="mt-5"
              styleType="primary_btn"
              :btnText="$t('BUTTONS.save')"
              :isLoading="isWaitingRequest"
              :disabled="isWaitingRequest"
            />
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import ChangableMapComponent from "@/components/general/ChangableMapComponent.vue";

export default {
  name: "CreateProvider",
  components: { ChangableMapComponent },

  data() {
    return {
      isWaitingRequest: false,
      autocomplete: null,

      data: {
        name: "",
        mobile: "",
        email: "",
        password: "",
        password_confirmation: "",
        avatar: { path: null, file: null, storagePath: null, url: null },
        activity_type: null,
        warehouse_address: "",
        address: "",
        lat: 24.7136,
        lng: 46.6753,
        commercial_registration_image: {
          path: null,
          file: null,
          storagePath: null,
          url: null,
        },
        tax_registration_image: {
          path: null,
          file: null,
          storagePath: null,
          url: null,
        },
      },
    };
  },

  computed: {
    activityTypes() {
      return [
        {
          id: 1,
          name: this.$t("PLACEHOLDERS.logistics_company"),
          value: "logistics_company",
        },
        {
          id: 2,
          name: this.$t("PLACEHOLDERS.online_store"),
          value: "ecommerce_store",
        },
        {
          id: 3,
          name: this.$t("PLACEHOLDERS.traditional_store"),
          value: "traditional_store",
        },
        {
          id: 4,
          name: this.$t("PLACEHOLDERS.individual"),
          value: "individual",
        },
      ];
    },
  },

  watch: {
    "data.lat": function() {
      this.reverseGeocode();
    },
    "data.lng": function() {
      this.reverseGeocode();
    },
  },

  methods: {
    // Reverse geocode coordinates to get address
    reverseGeocode() {
      if (!window.google || !window.google.maps) return;

      const geocoder = new google.maps.Geocoder();
      const location = {
        lat: parseFloat(this.data.lat),
        lng: parseFloat(this.data.lng),
      };

      geocoder.geocode({ location }, (results, status) => {
        if (status === "OK" && results[0]) {
          this.data.address = results[0].formatted_address;
        }
      });
    },

    // Initialize Google Places Autocomplete
    initAutocomplete() {
      if (window.google && window.google.maps) {
        this.autocomplete = new google.maps.places.Autocomplete(
          this.$refs.searchInput,
          { types: ["geocode"] }
        );

        this.autocomplete.addListener("place_changed", this.onPlaceChanged);
      }
    },

    // Handle place selection from autocomplete
    onPlaceChanged() {
      const place = this.autocomplete.getPlace();

      if (place.geometry) {
        this.data.lat = place.geometry.location.lat();
        this.data.lng = place.geometry.location.lng();
        this.data.address = place.formatted_address;
      }
    },

    selectAvatar(selectedImage) {
      this.data.avatar = selectedImage;
    },

    selectCommercialImage(selectedImage) {
      this.data.commercial_registration_image = selectedImage;
    },

    selectTaxImage(selectedImage) {
      this.data.tax_registration_image = selectedImage;
    },

    validateFormInputs() {
      if (this.data.password !== this.data.password_confirmation) {
        this.$message.error(this.$t("MESSAGES.passwordsMismatch"));
        return;
      }
      this.isWaitingRequest = true;
      this.submitForm();
    },

    async submitForm() {
      const formData = new FormData();

      // Personal Data
      formData.append("name", this.data.name);
      formData.append("mobile", this.data.mobile);
      formData.append("email", this.data.email);
      formData.append("password", this.data.password);
      formData.append("password_confirmation", this.data.password_confirmation);

      if (this.data.avatar.storagePath) {
        formData.append("avatar", this.data.avatar.storagePath);
      }

      // Provider Info
      if (this.data.activity_type) {
        formData.append("provider_info[type]", this.data.activity_type.value);
      }
      formData.append(
        "provider_info[warehouse_address]",
        this.data.warehouse_address
      );
      formData.append("provider_info[address]", this.data.address);
      formData.append("provider_info[lat]", this.data.lat);
      formData.append("provider_info[lng]", this.data.lng);

      if (this.data.commercial_registration_image.storagePath) {
        formData.append(
          "provider_info[commercial_registration_image]",
          this.data.commercial_registration_image.storagePath
        );
      }
      if (this.data.tax_registration_image.storagePath) {
        formData.append(
          "provider_info[tax_registration_image]",
          this.data.tax_registration_image.storagePath
        );
      }

      try {
        await this.$axios.post("/providers", formData);
        this.isWaitingRequest = false;
        this.$message.success(this.$t("MESSAGES.addedSuccessfully"));
        this.$router.push("/providers/all");
      } catch (error) {
        this.isWaitingRequest = false;
        this.$message.error(
          error.response?.data?.message || "An error occurred"
        );
      }
    },
  },

  mounted() {
    // Initialize autocomplete after component is mounted
    this.$nextTick(() => {
      this.initAutocomplete();
    });
  },

  beforeDestroy() {
    // Clean up autocomplete listener
    if (this.autocomplete) {
      google.maps.event.clearInstanceListeners(this.autocomplete);
    }
  },
};
</script>

<style scoped>
.section-title {
  color: #ef3405;
  font-weight: bold;
  padding-bottom: 10px;
  border-bottom: 2px solid #ef3405;
  margin-bottom: 15px;
}
</style>
